import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AgentPicker } from '@/components/AgentPicker'
import { WsPageShell } from '@/components/WsPageShell'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Field, Input, SelectField } from '@/ui/Field/Field'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ApiError, NetworkError } from '../api/client'
import { topicsApi } from '../api/topics'
import { ROUTES } from '../constants/routes'
import { topicsCopy } from '../copy'
import {
  topicQueryKeys,
  useMeQuery,
  useWorkspaceAgentsQuery,
} from '../hooks/queries'
import { useGoBack } from '../hooks/useGoBack'
import type { TopicInfo } from '../types/api'
import { normalizeTopicName, validateTopicName } from '../utils/topicNames'
import styles from './NewTopicPage.module.css'

type CreateFeedback =
  | { kind: 'error' | 'unknown'; message: string }
  | { kind: 'matching-topic'; message: string; topic: TopicInfo }

function isAmbiguousCreateError(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof ApiError && error.status >= 500 && !error.isStructured)
  )
}

export function NewTopicPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const goBack = useGoBack()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [selectedAgents, setSelectedAgents] = useState<string[]>([])
  const [primaryAgentId, setPrimaryAgentId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<CreateFeedback | null>(null)
  const seededWorkspaceRef = useRef<string | null>(null)
  const previousWorkspaceRef = useRef<string | undefined>(workspaceId)

  const agentsQuery = useWorkspaceAgentsQuery(workspaceId, {
    staleTime: 60_000,
  })
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data])
  const { data: me } = useMeQuery()
  const showSkeleton = useSkeletonDelay(agentsQuery.isPending)

  useEffect(() => {
    if (previousWorkspaceRef.current === workspaceId) return
    previousWorkspaceRef.current = workspaceId
    seededWorkspaceRef.current = null
    setSelectedAgents([])
    setPrimaryAgentId('')
    setNameError(null)
    setFeedback(null)
  }, [workspaceId])

  useEffect(() => {
    if (
      !workspaceId ||
      !agentsQuery.isSuccess ||
      seededWorkspaceRef.current === workspaceId
    ) {
      return
    }
    seededWorkspaceRef.current = workspaceId
    setSelectedAgents(agents.map((agent) => agent.agent_id))
  }, [agents, agentsQuery.isSuccess, workspaceId])

  const primaryCandidates = agents.filter(
    (agent) =>
      agent.status === 'active' && selectedAgents.includes(agent.agent_id),
  )
  const effectivePrimary = primaryCandidates.some(
    (agent) => agent.agent_id === primaryAgentId,
  )
    ? primaryAgentId
    : ''

  function updateName(value: string) {
    setName(value)
    setNameError(null)
    setFeedback(null)
  }

  function toggleAgent(id: string) {
    if (selectedAgents.includes(id) && primaryAgentId === id) {
      setPrimaryAgentId('')
    }
    setSelectedAgents((previous) => {
      if (previous.includes(id)) {
        return previous.filter((agentId) => agentId !== id)
      }
      return [...previous, id]
    })
  }

  function focusNameError() {
    queueMicrotask(() => document.getElementById('topic-name')?.focus())
  }

  async function reconcileAmbiguousCreate(normalizedName: string) {
    if (!workspaceId) return
    try {
      const topics = await topicsApi.listForWorkspace(workspaceId, {
        noCache: true,
      })
      const matches = topics.filter(
        (topic) => normalizeTopicName(topic.name) === normalizedName,
      )
      if (matches.length === 1) {
        setFeedback({
          kind: 'matching-topic',
          message: topicsCopy.newTopicPage.matchingTopic,
          topic: matches[0],
        })
        return
      }
    } catch {
      // The reconciliation read is best-effort; failure remains unknown.
    }
    setFeedback({
      kind: 'unknown',
      message: topicsCopy.newTopicPage.unconfirmed,
    })
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!workspaceId || !agentsQuery.isSuccess || submitting) return

    const validation = validateTopicName(name)
    if (!validation.ok) {
      setNameError(
        validation.reason === 'empty'
          ? topicsCopy.nameEmpty
          : topicsCopy.nameTooLong,
      )
      focusNameError()
      return
    }

    setNameError(null)
    setFeedback(null)
    setSubmitting(true)
    try {
      const created = await topicsApi.create(workspaceId, {
        name: validation.name,
        agents: selectedAgents,
        primary_agent_id: primaryAgentId || undefined,
      })
      await queryClient.invalidateQueries({
        queryKey: topicQueryKeys.workspace(workspaceId),
      })
      void navigate(ROUTES.topic(created.topic_id))
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setNameError(topicsCopy.newTopicPage.nameTaken)
        focusNameError()
      } else if (isAmbiguousCreateError(error)) {
        await reconcileAmbiguousCreate(validation.name)
      } else {
        setFeedback({
          kind: 'error',
          message:
            error instanceof Error && error.message
              ? error.message
              : topicsCopy.createFailed,
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const normalizedName = validateTopicName(name)

  return (
    <WsPageShell
      crumbs={[
        {
          label:
            me?.workspaces.find((w) => w.workspace_id === workspaceId)?.name ??
            topicsCopy.workspaceCrumb,
          to: ROUTES.home,
        },
        { label: topicsCopy.newTopic },
      ]}
      title={topicsCopy.newTopic}
      description={topicsCopy.newTopicDescription}
    >
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className={styles.column}
      >
        <Stack gap="space-16">
          <Field
            id="topic-name"
            label={topicsCopy.nameLabel}
            hint={topicsCopy.requiredHint}
            error={nameError ?? undefined}
            required
            className={styles.name}
          >
            <Input
              value={name}
              onChange={(e) => updateName(e.target.value)}
              autoFocus
            />
          </Field>

          {agentsQuery.isPending && (
            <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
          )}
          {agentsQuery.isError && (
            <Alert tone={agentsQuery.data ? 'warning' : 'danger'}>
              <Inline gap="space-8" align="baseline">
                <Text>{topicsCopy.newTopicPage.agentsLoadFailed}</Text>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void agentsQuery.refetch()}
                >
                  {topicsCopy.retry}
                </Button>
              </Inline>
            </Alert>
          )}

          {agentsQuery.data && (
            <AgentPicker
              legend={topicsCopy.newTopicPage.agentsLegend}
              agents={agents}
              selected={selectedAgents}
              onToggle={toggleAgent}
              emptyText={topicsCopy.newTopicPage.agentsEmpty}
            />
          )}

          {agentsQuery.data && agents.length > 0 && (
            <div className={styles.select}>
              <SelectField
                id="topic-primary-agent"
                label={topicsCopy.primaryLabel}
                hint={topicsCopy.newTopicPage.primaryOptional}
                help={topicsCopy.primaryHelp}
                value={effectivePrimary}
                onValue={setPrimaryAgentId}
              >
                <option value="">{topicsCopy.newTopicPage.primaryAuto}</option>
                {primaryCandidates.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.name}
                  </option>
                ))}
              </SelectField>
            </div>
          )}

          {feedback?.kind === 'error' && (
            <Alert tone="danger">{feedback.message}</Alert>
          )}
          {feedback?.kind === 'unknown' && (
            <Alert tone="warning">{feedback.message}</Alert>
          )}
          {feedback?.kind === 'matching-topic' && (
            <Stack gap="space-8" align="start">
              <Alert tone="info">{feedback.message}</Alert>
              <Link href={ROUTES.topic(feedback.topic.topic_id)}>
                {topicsCopy.newTopicPage.openTopic}
              </Link>
            </Stack>
          )}

          <Inline gap="space-8">
            <Button
              type="submit"
              variant="primary"
              disabled={
                !workspaceId || !agentsQuery.isSuccess || !normalizedName.ok
              }
              loading={submitting}
            >
              {topicsCopy.newTopicPage.create}
            </Button>
            <Button type="button" variant="outline" onClick={goBack}>
              {topicsCopy.cancel}
            </Button>
          </Inline>
        </Stack>
      </form>
    </WsPageShell>
  )
}
